<template>
    <!-- Layout mode (router padre): el chrome lo aporta cada hija vía su propio <MainContent title="X">. -->
    <!-- :key="$route.path" (NO $route.fullPath) para remontar solo cuando cambia la ruta,
         no cuando cambia el query. Evita que el <router-view> se desmonte al cambiar
         ?search= o ?search_for= en deportistas, que hacía perder el foco del input. -->
    <router-view v-if="asLayout" :key="$route.path" />

    <!-- Page-wrapper mode: chrome (header + body) -->
    <div v-else class="tw:min-h-screen">
        <!-- Header UNIFICADO — solo cuando hay title. -->
        <div v-if="title" class="tw:px-5 tw:md:px-10 tw:pt-6">
            <Card custom-classes="tw:px-6 tw:py-4" :padding="0">
                <PageTitleComponent
                    :title="title"
                    :extra-text="description"
                    :atras="true"
                >
                    <template #select>
                        <!--
                          Wrapper persistente usado por tests E2E para
                          preguntar "¿hay selector dentro de page-title-select?"
                          sin que la respuesta sea trivial: el wrapper siempre
                          existe (2 instancias — una por slot responsive de
                          PageTitleComponent, desktop + mobile), pero el
                          SelectListaComponent dentro se omite cuando
                          hideSelect=true. Ver tests REQ-3a/3b/3c/4.
                        -->
                        <div data-cy="page-title-select">
                            <SelectListaComponent
                                v-if="listaId && !hideSelect"
                                :value="listaActiva"
                                class="tw:w-full tw:sm:min-w-[300px]"
                                @input="onCambiarLista"
                            />
                        </div>
                    </template>
                </PageTitleComponent>
            </Card>
        </div>

        <!-- Body — subtitle, search/filters, default slot, footer, skeleton. -->
        <div class="tw:px-5 tw:md:px-10 tw:pt-4 tw:pb-6">
            <div v-if="$slots.header" class="tw:mb-4">
                <slot name="header"></slot>
            </div>
            <Card v-if="subtitle" customClasses="tw:mb-6">
                <span class="tw:text-stone-500 tw:text-md tw:font-bold">
                    {{ subtitle }}
                </span>
            </Card>
            <Card v-if="searchable || $slots.filters" class="tw:mb-6">
                <div class="tw:flex tw:flex-col tw:lg:flex-row tw:gap-4 tw:justify-between">
                    <div v-if="searchable" class="tw:w-full tw:lg:max-w-xs">
                        <SearchComponent
                            v-model="model.search"
                            :disabled="disabled"
                        />
                    </div>
                    <div class="tw:flex tw:flex-wrap tw:gap-3">
                        <slot name="filters"></slot>
                    </div>
                </div>
            </Card>
            <div class="tw:w-full">
                <slot></slot>
            </div>
            <TableFooter
                v-if="Object.values(meta).length"
                class="tw:mt-6"
                :value="model"
                :meta="meta"
                @input="model = $event"
            />
            <!-- Pagination skeleton mientras carga -->
            <div v-else-if="loading" class="tw:mt-6 tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-200 tw:p-4">
                <div class="tw:flex tw:items-center tw:justify-between">
                    <SkeletonItem height="tw:h-6" customClass="tw:w-40 tw:rounded" />
                    <div class="tw:flex tw:gap-1">
                        <SkeletonItem v-for="n in 3" :key="'pag-' + n" height="tw:h-8" customClass="tw:w-8 tw:rounded" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { watch, computed, getCurrentInstance } from 'vue'

    import Card from '@/modules/admin/ui/Card.vue'
    import PageTitleComponent from '@/modules/admin/ui/PageTitleComponent.vue'
    import SearchComponent from '@/modules/admin/ui/SearchComponent.vue'
    import TableFooter from '@/modules/admin/ui/table/TableFooter.vue'
    import { SkeletonItem } from '@/modules/admin/ui/skeleton'
    import SelectListaComponent from '@/components/backend/components/listas_abonados/SelectListaComponent.vue';
    import { useListaSelection } from '@/composables/admin/bases/useListaSelection';
    import { useRoute } from '@/composables/common/router/useRouter';

    const props = defineProps({
        title: {
            type: String,
            default: ''
        },
        /**
         * Short description rendered directly under the page title
         * (forwarded to PageTitleComponent.extraText). Unlike `subtitle`
         * — which renders as its own card after the header slot — this
         * belongs to the title block.
         */
        description: {
            type: String,
            default: ''
        },
        subtitle: String,
        value: Object,
        meta: {
            type: Object,
            default: () => ({})
        },
        searchable: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        asLayout: {
            type: Boolean,
            default: false
        }
    })

    const emit = defineEmits(['input', 'updated'])

    const model = computed({
        get: () => props.value || {},
        set: val => emit('input', val)
    })

    const { listaId, listaActiva, cambiarLista } = useListaSelection();

    // Hide SelectListaComponent on detail/edit/create routes (user shouldn't
    // change temporada mid-edit). Two mechanisms, evaluated in order:
    //   1. Explicit meta override: meta.hideSelect === true forces hide.
    //   2. Auto by route name: regex matches verb suffixes on the route name.
    // Fallback to useRoute() mirrors the pattern in useListaSelection.js so
    // unit tests (where Vue Router is not installed) still resolve $route
    // from the stubbed composable.
    const vm = getCurrentInstance();
    const route = useRoute();

    const hideSelect = computed(() => {
        const $route = (vm && vm.proxy && vm.proxy.$route) || route;
        if (!$route) return true;
        if ($route.meta && $route.meta.hideSelect === true) return true;
        const name = $route.name || '';
        return /\.(crear|show|editar|update|resumen)\b/i.test(name);
    });

    function onCambiarLista(lista) {
        cambiarLista(lista);
    }

    watch(
        () => [
            model.value.page,
            model.value.search,
            model.value.per_page,
            model.value.search_for,
            model.value.categoria,
            model.value.metodo_pago,
            model.value.equipo,
        ],
        (next, prev) => {
            const [nextPage, nextSearch, nextPerPage] = next
            const [prevPage, prevSearch, prevPerPage] = prev || []

            if (nextSearch !== prevSearch || nextPerPage !== prevPerPage) {
                if (nextPage !== 1) {
                    setPage(1)
                    return
                }
            }

            emit('updated')
        }
    )

    function setPage(page) {
        if (model.value && model.value.page !== page) {
            emit('input', { ...model.value, page })
        }
    }
</script>
